import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DataObjectIcon from '@mui/icons-material/DataObject';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FolderIcon from '@mui/icons-material/Folder';
import PeopleIcon from '@mui/icons-material/People';
import ReplayIcon from '@mui/icons-material/Replay';
import StorageIcon from '@mui/icons-material/Storage';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import axios from 'axios';
import React, { useEffect, useState } from 'react';
import AdminPageWrapper from './AdminPageWrapper';
import { dataMigrationHelpItems } from '../../utils/helpItems';

const DataMigration = ({ darkMode, toggleDarkMode, themeVariant }) => {
  const [migrationStatus, setMigrationStatus] = useState({
    inProgress: false,
    progress: 0,
    total: 0,
    completed: 0,
    failed: 0,
    lastRun: null,
    stats: {}
  });
  
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    migrationType: ''
  });
  
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const [rollbackDialog, setRollbackDialog] = useState({
    open: false,
    type: '',
    keepRoot: true
  });

  useEffect(() => {
    fetchMigrationStatus();
  }, []);

  const fetchMigrationStatus = async () => {
    try {
      const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/v3/migration/status`,
        {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Credentials': true,
            'email': authUserEmail,
            'auth': authToken,
          }
        }
      );
      
      setMigrationStatus(prevState => ({
        ...prevState,
        ...response.data,
        inProgress: response.data.inProgress || false
      }));
      
      // If migration is in progress, poll for updates
      if (response.data.inProgress) {
        setTimeout(fetchMigrationStatus, 30000);
      }
    } catch (error) {
      console.error('Failed to fetch migration status:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch migration status. Please try again later.',
        severity: 'error'
      });
    }
  };

  const handleMigrationStart = (migrationType) => {
    setConfirmDialog({
      open: true,
      migrationType
    });
  };

  const handleConfirmMigration = async () => {
    try {
      const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      const { migrationType } = confirmDialog;
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/migration/${migrationType}`,
        {},
        {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Credentials': true,
            'email': authUserEmail,
            'auth': authToken,
          }
        }
      );
      
      setSnackbar({
        open: true,
        message: `${migrationType.charAt(0).toUpperCase() + migrationType.slice(1)} migration started successfully!`,
        severity: 'success'
      });
      
      // Update status with new information
      setMigrationStatus(prevState => ({
        ...prevState,
        inProgress: true,
        progress: 0,
        completed: 0,
        failed: 0
      }));
      
      // Start polling for updates
      setTimeout(fetchMigrationStatus, 2000);
    } catch (error) {
      console.error(`Failed to start ${confirmDialog.migrationType} migration:`, error);
      setSnackbar({
        open: true,
        message: `Failed to start migration. Please try again.`,
        severity: 'error'
      });
    } finally {
      setConfirmDialog({ open: false, migrationType: '' });
    }
  };

  const handleRollbackStart = (type) => {
    setRollbackDialog({
      open: true,
      type,
      keepRoot: true
    });
  };

  const handleConfirmRollback = async () => {
    try {
      const authUserEmail = JSON.parse(localStorage.getItem('authUser')).email;
      const authToken = JSON.parse(localStorage.getItem('authToken'));
      const { type, keepRoot } = rollbackDialog;
      
      // Call rollback API
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/v3/migration/rollback`,
        { 
          // Send the correct type parameter based on selection
          type: type === 'all' ? 'all' : type,
          // When keepRoot is true, we want to keep the root structure
          keepRoot: keepRoot
        },
        {
          headers: {
            'Access-Control-Allow-Origin': '*',
            'Access-Control-Allow-Credentials': true,
            'email': authUserEmail,
            'auth': authToken,
          }
        }
      );
      
      setSnackbar({
        open: true,
        message: type === 'all' 
          ? 'Successfully rolled back all migrations!' 
          : `Successfully rolled back ${type} migration!`,
        severity: 'success'
      });
      
      // Refresh status to show updated counts
      fetchMigrationStatus();
    } catch (error) {
      console.error(`Failed to rollback ${rollbackDialog.type} migration:`, error);
      setSnackbar({
        open: true,
        message: `Failed to rollback migration: ${error.response?.data?.message || error.message}`,
        severity: 'error'
      });
    } finally {
      setRollbackDialog({ open: false, type: '', keepRoot: true });
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({
      ...prev,
      open: false
    }));
  };

  const handleConfirmDialogClose = () => {
    setConfirmDialog({ open: false, migrationType: '' });
  };

  const handleRollbackDialogClose = () => {
    setRollbackDialog({ open: false, type: '', keepRoot: true });
  };

  const getProgressColor = (progress) => {
    if (progress < 30) return 'error';
    if (progress < 70) return 'warning';
    return 'success';
  };

  return (
    <AdminPageWrapper
      title="Data Migration"
      helpItems={dataMigrationHelpItems}
      darkMode={darkMode}
      toggleDarkMode={toggleDarkMode}
      themeVariant={themeVariant}
    >
      <Container sx={{ mt: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" component="h1" gutterBottom>
            Data Migration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Migrate data between storage systems and perform database maintenance.
          </Typography>
          
          {/* Migration order instructions */}
          <Alert severity="info" sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              Important: Migration Order
            </Typography>
            <Typography variant="body2">
              For successful migration, please follow this exact order:
            </Typography>
            <Box component="ol" sx={{ mt: 1, mb: 0, pl: 2 }}>
              <li><Typography variant="body2"><strong>Users</strong> - Migrate user accounts first</Typography></li>
              <li><Typography variant="body2"><strong>Folders</strong> - Migrate folder structure second</Typography></li>
              <li><Typography variant="body2"><strong>Videos</strong> - Migrate video content last</Typography></li>
            </Box>
          </Alert>
        </Box>
        
        {migrationStatus.inProgress && (
          <Alert 
            severity="info" 
            sx={{ mb: 3 }}
            action={
              <CircularProgress 
                size={24} 
                color="inherit" 
                sx={{ ml: 2 }} 
              />
            }
          >
            Migration in progress: {migrationStatus.progress}% complete
            <LinearProgress 
              variant="determinate" 
              value={migrationStatus.progress} 
              color={getProgressColor(migrationStatus.progress)}
              sx={{ mt: 1, borderRadius: 1 }} 
            />
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* User Migration Card (Step 1) */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} className="user-data-migration">
              <Box 
                sx={{ 
                  
                  backgroundColor: '#0D4B79', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 36, 
                  height: 36, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  border: '2px solid white',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.25)'
                }}
              >
                1
              </Box>
              <CardContent sx={{ pt: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <PeopleIcon sx={{ fontSize: 36, color: '#0D4B79', mr: 2 }} />
                  <Typography variant="h6">
                    Step 1: User Migration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body1" paragraph>
                  Migrate user data from SQL database to MongoDB, including profiles, permissions, and language access rights.
                </Typography>
                
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    Migration Statistics:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Source Users (SQL):</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.sqlUsers || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Target Users (MongoDB):</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.mongoUsers || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Migration Progress:</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={getProgressColor(
                        migrationStatus.stats.sqlUsers ? 
                        (migrationStatus.stats.mongoUsers / migrationStatus.stats.sqlUsers) * 100 : 
                        0
                      )}
                    >
                      {migrationStatus.stats.sqlUsers ? 
                        Math.round((migrationStatus.stats.mongoUsers / migrationStatus.stats.sqlUsers) * 100) + '%' : 
                        '0%'
                      }
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<CompareArrowsIcon />}
                  disabled={migrationStatus.inProgress}
                  onClick={() => handleMigrationStart('users')}
                  sx={{ flex: 1, mr: 1 }}
                >
                  Migrate Users
                </Button>
                
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<ReplayIcon />}
                  disabled={migrationStatus.inProgress || !migrationStatus.stats.mongoUsers || migrationStatus.stats.mongoUsers === 0}
                  onClick={() => handleRollbackStart('users')}
                  sx={{ flex: 0.5 }}
                >
                  Reset MongoDB Database
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Folder Structure Migration Card (Step 2) */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} className="file-system-migration">
              <Box 
                sx={{ 
                  
                  backgroundColor: '#0D4B79', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 36, 
                  height: 36, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  border: '2px solid white',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.25)'
                }}
              >
                2
              </Box>
              <CardContent sx={{ pt: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <FolderIcon sx={{ fontSize: 36, color: '#0D4B79', mr: 2 }} />
                  <Typography variant="h6">
                    Step 2: Folder Structure Migration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body1" paragraph>
                  Migrate folder structures and file organization from the legacy system to the new MongoDB file system.
                </Typography>
                
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    Migration Statistics:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Source Folders:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.sqlFileSystemNodes || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Target Folders:</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.mongoFileSystemNodes || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Migration Progress:</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={getProgressColor(
                        migrationStatus.stats.sqlFileSystemNodes ? 
                        (migrationStatus.stats.mongoFileSystemNodes / migrationStatus.stats.sqlFileSystemNodes) * 100 : 
                        0
                      )}
                    >
                      {migrationStatus.stats.sqlFileSystemNodes ? 
                        Math.round((migrationStatus.stats.mongoFileSystemNodes / migrationStatus.stats.sqlFileSystemNodes) * 100) + '%' : 
                        '0%'
                      }
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<CompareArrowsIcon />}
                  disabled={migrationStatus.inProgress}
                  onClick={() => handleMigrationStart('folders')}
                  sx={{ flex: 1, mr: 1 }}
                >
                  Migrate Folders
                </Button>
                
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<ReplayIcon />}
                  disabled={migrationStatus.inProgress || !migrationStatus.stats.mongoFileSystemNodes || migrationStatus.stats.mongoFileSystemNodes === 0}
                  onClick={() => handleRollbackStart('filesystem')}
                  sx={{ flex: 0.5 }}
                >
                  Reset MongoDB Database
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Video Migration Card (Step 3) */}
          <Grid item xs={12} md={6} >
            <Card elevation={3} className="video-data-migration">
              <Box 
                sx={{ 
                  
                  backgroundColor: '#0D4B79', 
                  color: 'white', 
                  borderRadius: '50%', 
                  width: 36, 
                  height: 36, 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  border: '2px solid white',
                  boxShadow: '0px 2px 4px rgba(0,0,0,0.25)'
                }}
              >
                3
              </Box>
              <CardContent sx={{ pt: 3 }}>
                <Box display="flex" alignItems="center" mb={2}>
                  <VideoFileIcon sx={{ fontSize: 36, color: '#0D4B79', mr: 2 }} />
                  <Typography variant="h6">
                    Step 3: Video Migration
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                <Typography variant="body1" paragraph>
                  Migrate video data from SQL database to MongoDB, including subtitles, metadata, and bookmarks.
                </Typography>
                
                <Stack spacing={2}>
                  <Typography variant="subtitle2">
                    Migration Statistics:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Source Videos (SQL):</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.sqlVideos || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Target Videos (MongoDB):</Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {migrationStatus.stats.mongoVideos || '0'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2">Migration Progress:</Typography>
                    <Typography 
                      variant="body2" 
                      fontWeight="bold"
                      color={getProgressColor(
                        migrationStatus.stats.sqlVideos ? 
                        (migrationStatus.stats.mongoVideos / migrationStatus.stats.sqlVideos) * 100 : 
                        0
                      )}
                    >
                      {migrationStatus.stats.sqlVideos ? 
                        Math.round((migrationStatus.stats.mongoVideos / migrationStatus.stats.sqlVideos) * 100) + '%' : 
                        '0%'
                      }
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
              <CardActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Button 
                  variant="contained" 
                  color="primary"
                  startIcon={<CompareArrowsIcon />}
                  disabled={migrationStatus.inProgress}
                  onClick={() => handleMigrationStart('videos')}
                  sx={{ flex: 1, mr: 1 }}
                >
                  Migrate Videos
                </Button>
                
                <Button 
                  variant="outlined" 
                  color="error"
                  startIcon={<ReplayIcon />}
                  disabled={migrationStatus.inProgress || !migrationStatus.stats.mongoVideos || migrationStatus.stats.mongoVideos === 0}
                  onClick={() => handleRollbackStart('videos')}
                  sx={{ flex: 0.5 }}
                >
                  Reset MongoDB Database
                </Button>
              </CardActions>
            </Card>
          </Grid>

          {/* Migration Status Card */}
          <Grid item xs={12} md={6}>
            <Card elevation={3} className="migration-status">
              <CardContent>
                <Box display="flex" alignItems="center" mb={2}>
                  <StorageIcon sx={{ fontSize: 36, color: '#0D4B79', mr: 2 }} />
                  <Typography variant="h6">
                    Migration Status
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {migrationStatus.lastRun ? (
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Last Migration:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {new Date(migrationStatus.lastRun).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Total Items:</Typography>
                      <Typography variant="body2" fontWeight="bold">
                        {migrationStatus.total}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Successfully Migrated:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="success.main">
                        {migrationStatus.completed}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">Failed Migrations:</Typography>
                      <Typography variant="body2" fontWeight="bold" color="error.main">
                        {migrationStatus.failed}
                      </Typography>
                    </Box>
                  </Stack>
                ) : (
                  <Typography variant="body1" color="text.secondary" sx={{ py: 2 }}>
                    No migration has been run yet.
                  </Typography>
                )}
              </CardContent>
              <CardActions>
                <Button 
                  variant="outlined" 
                  color="primary"
                  startIcon={<DataObjectIcon />}
                  onClick={fetchMigrationStatus}
                  fullWidth
                >
                  Refresh Status
                </Button>
              </CardActions>
            </Card>
          </Grid>
        </Grid>

        {/* Confirmation Dialog */}
        <Dialog
          open={confirmDialog.open}
          onClose={handleConfirmDialogClose}
        >
          <DialogTitle>
            Confirm Migration
          </DialogTitle>
          <DialogContent>
            <DialogContentText>
              You are about to start migrating {confirmDialog.migrationType}.
              This process may take some time and will run in the background.
              Are you sure you want to continue?
            </DialogContentText>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="Data will be safely transferred between systems" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="success" />
                </ListItemIcon>
                <ListItemText primary="The process can be monitored from this page" />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <ErrorOutlineIcon color="warning" />
                </ListItemIcon>
                <ListItemText primary="Existing data will not be overwritten by default" />
              </ListItem>
            </List>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleConfirmDialogClose}>Cancel</Button>
            <Button 
              onClick={handleConfirmMigration} 
              color="primary"
              variant="contained"
              autoFocus
            >
              Start Migration
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rollback Confirmation Dialog */}
        <Dialog
          open={rollbackDialog.open}
          onClose={handleRollbackDialogClose}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            <WarningIcon color="error" sx={{ mr: 1 }} />
            {rollbackDialog.type === 'all' ? 'Rollback All Migrations' : `Rollback ${rollbackDialog.type} Migration`}
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              You are about to <strong>roll back {rollbackDialog.type === 'all' ? 'all migrations' : `the ${rollbackDialog.type} migration`}</strong>. 
              This will <strong>delete {rollbackDialog.type === 'all' ? 'all' : `all ${rollbackDialog.type}`} data</strong> from MongoDB.
              This action cannot be undone!
            </DialogContentText>
            
            {/* Show the switch only for filesystem rollbacks or all rollbacks */}
            {(rollbackDialog.type === 'filesystem' || rollbackDialog.type === 'all') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={rollbackDialog.keepRoot}
                    onChange={(e) => setRollbackDialog(prev => ({ ...prev, keepRoot: e.target.checked }))}
                    color="primary"
                  />
                }
                label={
                  <>
                    Keep root node structure <strong>(recommended)</strong>
                    <Typography variant="caption" display="block" color="text.secondary">
                      When checked, the base folder structure will be preserved
                    </Typography>
                  </>
                }
              />
            )}
            
            <Alert severity="warning" sx={{ mt: 2 }}>
              This is a destructive operation that will permanently delete data.
              Please make sure you have backups if needed.
            </Alert>
            
            {/* Update the list item text for clarity */}
            <List>
              <ListItem>
                <ListItemIcon>
                  <ErrorOutlineIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary={rollbackDialog.type === 'all' 
                    ? "All migrated data in MongoDB will be deleted" 
                    : `All ${rollbackDialog.type} data in MongoDB will be deleted`} 
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={rollbackDialog.type === 'filesystem' && rollbackDialog.keepRoot
                    ? "Root folder structure will be preserved"
                    : "Original SQL database will not be affected"}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckCircleOutlineIcon color="primary" />
                </ListItemIcon>
                <ListItemText primary="You can re-run the migration after rollback" />
              </ListItem>
            </List>
          </DialogContent>
          {/* Update the button text for clarity */}
          <DialogActions>
            <Button onClick={handleRollbackDialogClose}>Cancel</Button>
            <Button 
              onClick={handleConfirmRollback} 
              color="error"
              variant="contained"
            >
              {rollbackDialog.type === 'all' ? 'Rollback All' : `Rollback ${rollbackDialog.type}`}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Status snackbar */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Alert 
            onClose={handleSnackbarClose} 
            severity={snackbar.severity} 
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Container>
    </AdminPageWrapper>
  );
};

export default DataMigration;